import {
  CalciteButton,
  CalciteDropdown,
  CalciteDropdownItem,
} from '@esri/calcite-components-react';
import Color from 'color';
import { useMemo, useState } from 'react';
import { createGlobalStyle } from 'styled-components';
import { useDebounce, useLocalStorage } from 'usehooks-ts';

export type AppTheme = {
  name: string;
  primary: Color;
  border: Color;
  background: Color;
  text: Color;
};

const themes: AppTheme[] = [
  {
    name: 'Light',
    primary: Color('#007AC2'),
    border: Color('#cacaca'),
    background: Color('#ffffff'),
    text: Color('#151515'),
  },
  {
    name: 'Dark',
    primary: Color('#009AF2'),
    border: Color('#393939'),
    background: Color('#2b2b2b'),
    text: Color('#ffffff'),
  },
  {
    name: 'Kablooey',
    primary: Color('#308e29'),
    border: Color('#d8d8d7'),
    background: Color('#fdf0d5'),
    text: Color('#3a3335'),
  },

  {
    name: 'Kablooey Dark',
    primary: Color('#3cb233'),
    border: Color('#3a3335'),
    background: Color('#3a3335'),
    text: Color('#fdf0d5'),
  },

  {
    name: 'Midnight Blue',
    primary: Color('#00F1A1'),
    border: Color('#011926'),
    background: Color('#011926'),
    text: Color('#7FD1E3'),
  },

  {
    name: 'Sunny',
    primary: Color('#F4D35E'),
    border: Color('#cfda7c'),
    background: Color('#FFF8E6'),
    text: Color('#5A5A5A'),
  },

  {
    name: 'Rose',
    primary: Color('#FFC2B4'),
    border: Color('#D4AFB9'),
    background: Color('#F9E4E8'),
    text: Color('#5A5A5A'),
  },

  {
    name: 'Forest',
    primary: Color('#00755F'),
    border: Color('#D9D9D6'),
    background: Color('#F2F2F2'),
    text: Color('#3C3C3C'),
  },

  {
    name: 'Retro',
    primary: Color('#B88A3E'),
    border: Color('#3F3F3F'),
    background: Color('#EFEFEF'),
    text: Color('#3F3F3F'),
  },

  {
    name: 'High Contrast',
    primary: Color('#007AC2'),
    border: Color('#ffffff'),
    background: Color('#1e1e1e'),
    text: Color('#ffffff'),
  },

  {
    name: 'Pastel',
    primary: Color('#A9DEF9'),
    border: Color('#E5E5E5'),
    background: Color('#F7F7F7'),
    text: Color('#555555'),
  },
];

export const generateColors = ({
  primary,
  border,
  text,
  background,
}: AppTheme): Record<string, string> => {
  const light = background.isLight();

  const brandHover = primary.darken(light ? 0.203 : 0.25).hex();

  return {
    '--calcite-ui-brand': primary.hex(),
    '--calcite-ui-brand-hover': brandHover,
    '--calcite-ui-brand-press': primary.darken(light ? 0.4 : 0.375).hex(),

    '--calcite-ui-text-1': text.hex(),
    '--calcite-ui-text-2': text.lighten(light ? 0.05 : 0.25).hex(),
    '--calcite-ui-text-3': text.lighten(light ? 0.2 : 0.375).hex(),
    '--calcite-ui-text-highlight': primary.hex(),

    '--calcite-ui-text-inverse': light ? '#ffffff' : '#000000',

    '--calcite-ui-text-link': light ? brandHover : primary.hex(),
    '--calcite-link-blue-underline': primary.fade(0.5).rgb().toString(),

    '--calcite-ui-background': light
      ? background.darken(0.03).hex()
      : background.lighten(0.23).hex(),
    '--calcite-ui-foreground-1': background.hex(),
    '--calcite-ui-foreground-2': background.darken(light ? 0.05 : 0.25).hex(),
    '--calcite-ui-foreground-3': background.darken(light ? 0.07 : 0.55).hex(),
    '--calcite-ui-inverse': background.negate().hex(),

    '--calcite-ui-inverse-hover': background.negate().darken(0.2).hex(),
    '--calcite-ui-inverse-press': background.negate().darken(0.4).hex(),

    '--calcite-ui-border-1': border.hex(),
    '--calcite-ui-border-2': border.lighten(light ? 0.05 : 0.1).hex(),
    '--calcite-ui-border-3': border.lighten(light ? 0.105 : 0.2).hex(),
    '--calcite-ui-border-input': light
      ? border.darken(0.26).hex()
      : border.lighten(0.3).hex(),

    '--calcite-ui-icon-color': 'currentColor',

    '--calcite-ui-info': brandHover,
    '--calcite-ui-success': light ? '#308e29' : '#36DA43',
    '--calcite-ui-warning': light ? '#f1c21b' : '#FFC900',
    '--calcite-ui-danger': light ? '#D83020' : '#FE583E',
    '--calcite-ui-danger-hover': light ? '#A82B1E' : '#FF0015',
    '--calcite-ui-danger-press': light ? '#7C1D13' : '#D90012',
    '--rdg-color-scheme': light ? 'light' : 'dark',
    '--rdg-color': 'var(--calcite-ui-text-1)',
    '--rdg-border-color': 'var(--calcite-ui-border-3)',
    '--rdg-selection-color': 'var(--calcite-ui-brand)',
    '--rdg-summary-border-color': 'var(--calcite-ui-border-1)',
    '--rdg-background-color': 'var(--calcite-ui-foreground-1)',
    '--rdg-header-background-color': 'var(--calcite-ui-foreground-2)',
    '--rdg-row-hover-background-color': 'var(--calcite-ui-foreground-2)',
    '--rdg-row-selected-background-color': 'var(--calcite-ui-foreground-2)',
    '--rdg-row-selected-hover-background-color':
      'var(--calcite-ui-foreground-2)',
    '--rdg-checkbox-color': 'var(--calcite-ui-brand)',
    '--rdg-checkbox-focus-color': 'var(--calcite-ui-brand-hover)',
    '--rdg-checkbox-disabled-border-color': 'var(--calcite-ui-border-1)',
    '--rdg-checkbox-disabled-background-color': 'var(--calcite-ui-background)',
  };
};

const objectToCss = (obj: Record<string, string>) =>
  Object.entries(obj)
    .map(([key, value]) => `${key}: ${value};`)
    .join('\n      ');

const cssString = ({ appTheme }: { appTheme: AppTheme }) => {
  const mode = appTheme.background.isLight() ? 'light' : 'dark';
  const colors = generateColors(appTheme);

  return `
    .app-theme {
      color-scheme: ${mode};
      --calcite-mode-name: ${mode};
      ${objectToCss(colors)}
    }
  `;
};

const GlobalStyle = createGlobalStyle(cssString);

const defaultTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
  ? 'Dark'
  : 'Light';

export function ThemeManager() {
  const [themeName, setThemeName] = useLocalStorage<AppTheme['name']>(
    'table-editor-theme',
    defaultTheme
  );

  const theme = useMemo(() => {
    const theme = themes.find((t) => t.name === themeName);
    if (!theme) {
      setThemeName(defaultTheme);
      const backupTheme = themes.find((t) => t.name === defaultTheme);

      if (!backupTheme) {
        throw new Error('No default theme found');
      }

      return backupTheme;
    }
    return theme;
  }, [setThemeName, themeName]);

  const [previewTheme, setPreviewTheme] = useState<AppTheme>();

  const debouncedPreview = useDebounce(previewTheme, 100);

  return (
    <>
      <CalciteDropdown
        title="Theme Select"
        onCalciteDropdownClose={() => {
          setPreviewTheme(undefined);
        }}
        overlayPositioning="fixed"
      >
        <CalciteButton slot="trigger" color="neutral" width="full">
          {themeName}
        </CalciteButton>
        {themes.map((theme) => (
          <CalciteDropdownItem
            key={theme.name}
            selected={theme.name === themeName}
            onMouseEnter={() => setPreviewTheme({ ...theme })}
            onMouseLeave={() => setPreviewTheme(undefined)}
            onClick={() => setThemeName(theme.name)}
          >
            {theme.name}
          </CalciteDropdownItem>
        ))}
      </CalciteDropdown>
      <GlobalStyle appTheme={debouncedPreview ?? theme} />
    </>
  );
}
